import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { CustomerBasicInfo } from "@redotech/effect-api-shared/models/customer";
import {
  computeSupportTicketSnoozeUntil,
  type FixedSupportTicketSnoozeOption,
  type SupportTicketAssigneeType,
  type SupportTicketAssignmentUpdate,
  type SupportTicketListItemResponse,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";

export type MutableSupportTicketListItem = {
  -readonly [K in keyof SupportTicketListItemResponse]: SupportTicketListItemResponse[K];
};

export type SnoozeParams =
  | { duration: FixedSupportTicketSnoozeOption }
  | { duration: "custom"; until: Date }
  | null;

export const computeSnoozeUntil = computeSupportTicketSnoozeUntil;

export function applySupportTicketStatus(
  draft: MutableSupportTicketListItem,
  status: SupportTicketStatus,
): void {
  draft.status = status;
}

export function applySupportTicketReadState(
  draft: MutableSupportTicketListItem,
  readAt: Date | null,
): void {
  draft.readAt = readAt;
}

export function applySupportTicketSnooze(
  draft: MutableSupportTicketListItem,
  option: SnoozeParams,
): void {
  if (option === null) {
    draft.status = "OPEN";
    draft.snoozedUntil = null;
    return;
  }

  draft.status = "SNOOZED";
  draft.snoozedUntil =
    option.duration === "custom"
      ? option.until
      : computeSnoozeUntil(option.duration);
}

export function applySupportTicketPriority(
  draft: MutableSupportTicketListItem,
  priority: SupportTicketPriority | null,
): void {
  draft.priority = priority;
}

/**
 * Optimistically reflect a cosmetic email-subject change on the list row. The
 * row surfaces the subject as `emailSubject`; the change is persisted as a
 * `display_subject` override server-side via `toUpdateSupportTicketInput`.
 */
export function applySupportTicketEmailSubject(
  draft: MutableSupportTicketListItem,
  emailSubject: string,
): void {
  draft.emailSubject = emailSubject;
}

export function applySupportTicketAssignee(
  draft: MutableSupportTicketListItem,
  assigneeId: MongoUserId | null,
  assigneeType: SupportTicketAssigneeType | null = null,
): void {
  draft.mongoAssigneeId = assigneeId;
  draft.assigneeType = assigneeType;
}

/**
 * Maps an assignment update to the row fields that represent it, so callers can
 * optimistically reflect an assignment change on a list row. `assignee_type`
 * and `mongo_assignee_id` are independent columns, so both must be set together
 * to keep the row consistent (e.g. an AI assignment clears the user id).
 */
export function supportTicketAssignmentRowPatch(
  assignment: SupportTicketAssignmentUpdate,
): Pick<MutableSupportTicketListItem, "mongoAssigneeId" | "assigneeType"> {
  switch (assignment.assignmentType) {
    case "user":
      return { mongoAssigneeId: assignment.userId, assigneeType: "USER" };
    case "ai":
      return { mongoAssigneeId: null, assigneeType: "AI" };
    case "chatFlow":
      return { mongoAssigneeId: null, assigneeType: "CHAT_FLOW" };
    case "unassign":
      return { mongoAssigneeId: null, assigneeType: null };
  }
}

export function replaceSupportTicketTags(
  draft: MutableSupportTicketListItem,
  tagIds: ShopperAiTagId[],
): void {
  draft.tagIds = tagIds;
}

/**
 * Optimistic draft mutation for marking a ticket as spam — closed, with the spam
 * tag added (idempotent if already present).
 *
 * Marking spam runs its authoritative server change through the dedicated
 * `markSupportTicketSpam` RPC, so the caller applies this draft inside a
 * `createTransaction` whose `mutationFn` is that RPC. That routes persistence
 * through the RPC and bypasses the list collection's `onUpdate`, which would
 * otherwise fire a second, redundant `updateSupportTicket` PATCH whose failure
 * surfaced a false "network error" toast even though the RPC succeeded.
 */
export function applySpamOptimistic(
  draft: MutableSupportTicketListItem,
  spamTagId: ShopperAiTagId | null,
): void {
  applySupportTicketStatus(draft, "CLOSED");
  if (spamTagId && !draft.tagIds.includes(spamTagId)) {
    replaceSupportTicketTags(draft, [...draft.tagIds, spamTagId]);
  }
}

/**
 * Inverse of {@link applySpamOptimistic}: reopen the ticket and drop the spam
 * tag. Same rationale — the `unmarkSupportTicketSpam` RPC owns the authoritative
 * change and persistence is routed through it, not the collection's `onUpdate`.
 */
export function applyUnmarkSpamOptimistic(
  draft: MutableSupportTicketListItem,
  spamTagId: ShopperAiTagId | null,
): void {
  applySupportTicketStatus(draft, "OPEN");
  if (spamTagId) {
    replaceSupportTicketTags(
      draft,
      draft.tagIds.filter((id) => id !== spamTagId),
    );
  }
}

export type OptimisticCustomerInfo = Pick<
  CustomerBasicInfo,
  "displayName" | "email" | "phone"
>;

export function applySupportTicketCustomer(
  draft: MutableSupportTicketListItem,
  mongoCustomerId: MongoCustomerId | null,
  optimisticCustomer?: OptimisticCustomerInfo,
): void {
  draft.mongoCustomerId = mongoCustomerId;
  if (mongoCustomerId && optimisticCustomer) {
    draft.customer = {
      id: mongoCustomerId,
      displayName: optimisticCustomer.displayName,
      firstName: null,
      lastName: null,
      email: optimisticCustomer.email,
      phone: optimisticCustomer.phone,
      facebookAccounts: [],
      instagramAccounts: [],
    };
  } else {
    draft.customer = undefined;
  }
}
